import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../data/site";
import { inputCls, AdminButton } from "../ui";

const PAGE_LABELS = {
  "/": "Homepage",
  "/available-homes": "Homes for Sale",
  "/custom-homes": "Custom Homes & Floor Plans",
  "/our-work": "Projects / Our Work",
  "/our-process": "Our Process",
  "/about": "About",
  "/contact": "Contact",
};

const SeoPanel = () => {
  const { data = [], refetch } = useQuery({
    queryKey: ["seo-admin"],
    queryFn: async () => (await axios.get(`${API}/seo`, { withCredentials: true })).data,
  });
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState("");

  useEffect(() => {
    const r = {};
    data.forEach((d) => { r[d.path] = { title: d.title, description: d.description, image: d.image || "", noindex: !!d.noindex }; });
    setRows(r);
  }, [data]);

  const save = async (path) => {
    setSaving(path);
    await axios.put(`${API}/seo`, { path, ...rows[path] }, { withCredentials: true });
    toast.success(`SEO saved for ${PAGE_LABELS[path] || path}`);
    setSaving("");
    refetch();
  };

  return (
    <div data-testid="seo-panel">
      <p className="text-sm text-[#5C5A56] max-w-2xl mb-8">
        These control how each page appears in Google search results and when shared on social media.
        Titles under ~60 characters and descriptions under ~160 characters work best.
      </p>
      <div className="flex flex-col gap-6">
        {Object.keys(PAGE_LABELS).map((path) => {
          const r = rows[path] || { title: "", description: "", image: "", noindex: false };
          return (
            <div key={path} className="bg-white border border-[rgba(26,26,26,0.1)] p-6" data-testid={`seo-${path.replace(/\//g, "") || "home"}`}>
              <div className="flex items-center justify-between mb-4">
                <p className="font-serif text-xl">{PAGE_LABELS[path]} <span className="font-mono text-xs text-[#A3A09B] ml-2">{path}</span></p>
                <AdminButton onClick={() => save(path)} disabled={saving === path} testid={`seo-save-${path.replace(/\//g, "") || "home"}`}>
                  {saving === path ? "Saving…" : "Save"}
                </AdminButton>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="eyebrow text-[#5C5A56] block mb-1">Page Title</span>
                  <input value={r.title} onChange={(e) => setRows({ ...rows, [path]: { ...r, title: e.target.value } })} className={inputCls} data-testid={`seo-title-${path}`} />
                </label>
                <label className="block">
                  <span className="eyebrow text-[#5C5A56] block mb-1">Social Share Image URL (optional)</span>
                  <input value={r.image} onChange={(e) => setRows({ ...rows, [path]: { ...r, image: e.target.value } })} className={inputCls} data-testid={`seo-image-${path}`} />
                </label>
                <label className="block md:col-span-2">
                  <span className="eyebrow text-[#5C5A56] block mb-1">Meta Description</span>
                  <textarea rows={2} value={r.description} onChange={(e) => setRows({ ...rows, [path]: { ...r, description: e.target.value } })} className={inputCls} data-testid={`seo-desc-${path}`} />
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={r.noindex} onChange={(e) => setRows({ ...rows, [path]: { ...r, noindex: e.target.checked } })} className="w-4 h-4 accent-[#8C6A4F]" data-testid={`seo-noindex-${path}`} />
                  <span className="eyebrow text-[#5C5A56]">Hide this page from search engines</span>
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SeoPanel;
